import React, { useEffect, useCallback, useMemo, useReducer, useState } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { translate } from '@utils/i18n';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import FilterPanel from '@components/FilterPanel';
import CustomScrollbar from '@components/CustomScrollbar/CustomScrollbar';
import ColumnMenuContext from '@components/Kendo/ColumnMenuContext';
import ColumnMenu from '@components/Kendo/ColumnMenu';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import { Loader } from '@ui';
import { DeficiencyIdCell, InspectionIdCell, BuildingCell, DetailsCell, ResolvedCell } from './cells';
import { useDeficiencies } from './hooks/useDeficiencies';
import getFiltersList from './helpers/filtersList';
import { getInitialData, saveCacheToLocalStorage, getInitialFilters } from './helpers/initialData';
import { getColumnsConfig, getCacheKey, getColumnsCacheKey, ALL_COLUMNS } from './constants';
import { actionTypes, reducer } from './reducer';
import DeficiencyDrawer from './components/DeficiencyDrawer';
import './index.scss';
function DeficienciesIndex({ buildingOptions = [], buildingId }) {
    const cacheKey = getCacheKey(buildingId);
    const [state, dispatch] = useReducer(reducer, { cacheKey, buildingId }, getInitialData);
    const { page, sort, filters, columns, data, totalCount, loading } = state;
    const [selectedDeficiencyId, setSelectedDeficiencyId] = useState(null);
    const { data: queryData, isLoading, isFetching } = useDeficiencies({ page, sort, filters });
    useEffect(() => {
        saveCacheToLocalStorage(cacheKey, { page, sort, filters, columns }, buildingId);
    }, [cacheKey, buildingId, page, sort, filters, columns]);
    useEffect(() => {
        if (queryData) {
            dispatch({ type: actionTypes.DATA_LOADED, data: queryData });
        }
    }, [queryData]);
    useEffect(() => {
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: isLoading || isFetching });
    }, [isLoading, isFetching]);
    const filtersList = useMemo(() => getFiltersList({ filters, dispatch, buildingOptions, buildingId }), [filters, dispatch, buildingOptions, buildingId]);
    const handleResetFilters = useCallback(() => {
        dispatch({ type: actionTypes.FILTER_CHANGED, filters: getInitialFilters(buildingId) });
    }, [buildingId]);
    const handleResetCache = () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(translate('are_you_sure', { namespace: 'generic' })))
            return;
        if (cacheKey)
            localStorage.removeItem(cacheKey);
        localStorage.removeItem(getColumnsCacheKey(buildingId));
        window.location.reload();
    };
    const handleDeficiencyClick = useCallback((deficiencyId) => {
        setSelectedDeficiencyId(deficiencyId);
    }, []);
    const handleDrawerClose = useCallback(() => {
        setSelectedDeficiencyId(null);
    }, []);
    const DeficiencyIdCellWrapper = useCallback((props) => (React.createElement(DeficiencyIdCell
    // eslint-disable-next-line react/jsx-props-no-spreading
    , Object.assign({}, props, { onDeficiencyClick: handleDeficiencyClick }))), [handleDeficiencyClick]);
    const ALL_COLUMNS_DATA = useMemo(() => getColumnsConfig(DeficiencyIdCellWrapper, InspectionIdCell, BuildingCell, DetailsCell, ResolvedCell), [DeficiencyIdCellWrapper]);
    const allMenuColumns = useMemo(() => ALL_COLUMNS.map((column) => ALL_COLUMNS_DATA[column]), [ALL_COLUMNS_DATA]);
    const visibleColumns = useMemo(() => columns.map((column) => ALL_COLUMNS_DATA[column]), [columns, ALL_COLUMNS_DATA]);
    const onColumnsChange = useCallback((cols) => {
        const selectedColumns = cols.map((col) => col.id).filter((id) => Boolean(id));
        dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: selectedColumns });
    }, [dispatch]);
    const columnContextValue = useMemo(() => ({
        onColumnsChange,
        columnsState: visibleColumns,
        columns: allMenuColumns,
        dispatch
    }), [onColumnsChange, visibleColumns, allMenuColumns, dispatch]);
    return (React.createElement(React.Fragment, null,
        React.createElement(KendoLocalizationProvider, null,
            React.createElement(ColumnMenuContext.Provider, { value: columnContextValue },
                React.createElement(FilterPanel, { onResetFilters: handleResetFilters, onResetCache: handleResetCache, filters: filtersList }),
                loading && (React.createElement("div", { className: "workspace__section" },
                    React.createElement(Loader, { vertical: true, title: translate('loading', { namespace: 'generic' }) }))),
                !loading && (React.createElement(CustomScrollbar, null,
                    React.createElement(Grid, { data: data, total: totalCount, skip: page.skip, take: page.take, onPageChange: (event) => {
                            dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
                        }, pageable: { type: 'input', pageSizes: [25, 50, 100], pageSizeValue: page.take }, pager: EnterKeyPager, sortable: true, sort: sort, onSortChange: (event) => {
                            dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
                        }, dataItemKey: "id", className: "qmb-grid--01" }, visibleColumns.map((column) => (React.createElement(Column, { key: column.id, field: column.field, title: column.title, columnMenu: ColumnMenu, cell: column.cell, sortable: column.sortable, width: column.width, className: column.className, headerClassName: column.headerClassName })))))))),
        selectedDeficiencyId && React.createElement(DeficiencyDrawer, { deficiencyId: selectedDeficiencyId, onClose: handleDrawerClose })));
}
export default DeficienciesIndex;
